import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    Checkbox,
    Input,
    Radio,
    Select,
    Spin,
    Tooltip,
} from 'antd';
import { LoadingOutlined, QuestionCircleOutlined, TeamOutlined } from '@ant-design/icons';
import jaJP from 'antd/lib/date-picker/locale/ja_JP';
import * as Actions from 'flex/Actions';
import {
    applicationFormRules,
    DatePickerRangesIncludeInfinite,
    formRules,
    getDefaultLayout,
    getDisabledDate,
} from 'constants/GlobalConfig';
import { texts } from 'constants/texts';
import BaseForm from 'components/modules/BaseForm';
import BaseModal from 'components/modules/BaseModal';
import CustomRangePicker from 'components/modules/CustomRangePicker';
import { useSource } from '../SourceProvider';
import { applicationUtils } from '../ApplicationUtils';
import { FormCard } from '../../../AppTemplate/Forms/FormCard';
import Targets from '../../../Targets';
import { checkFormChange, convertDateToString } from 'utils/Helper';
import { useAppSelector } from 'flex/utils';
import { TargetGradesSelect } from 'components/modules/SelectCustom/TargetGradesSelect';

const Form = BaseForm.ModalForm;
const { Option } = Select;
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;


const layout = getDefaultLayout(true);
const formItemLayoutWithoutLabel = {
    wrapperCol: {
        xl: { span: 20, offset: 8 },
    },
};

const AppGroupModal = ({
    initialValue, isCreate, onCancel, onOk, loading, visible
}) => {
    const [form] = Form.useForm();
    const [source,] = useSource();
    const [isOpenDeliveryTargetModal, setIsOpenDeliveryTargetModal] = useState(false);
    const [isPublishToAllOrganizations, setIsPublishToAllOrganizations] = useState(true);
    const [selectedChoiceTemplate, setSelectedChoiceTemplate] = useState();
    const [formInitValue, setFormInitValue] = useState({});
    const [formChangeValue, setFormChangeValue] = useState({});
    const [isDisabledSubmitBtn, setIsDisabledSubmitBtn] = useState(false);
    const [isDesignatedTerm, setIsDesignatedTerm] = useState(false);
    const dispatch = useDispatch();
    const [currentTarget, setCurrentTarget] = useState({
        target_organizations: []
    });
    const useDeepLinking = Form.useWatch('useDeepLinking', form);
    const globalSelector = useAppSelector(state => state.globalState);

    const presetFieldsValue = () => {
        form.setFieldsValue(
            applicationUtils.initialValueOfApplicationCategory
        );

        setCurrentTarget({
            target_organizations: []
        });
        setIsPublishToAllOrganizations(true);
        setIsDesignatedTerm(false);
        setSelectedChoiceTemplate(undefined);
    };

    useEffect(() => {
        if (!visible) return;
        if (isCreate) {
            form.resetFields();
            presetFieldsValue();
        }
    }, [dispatch, visible]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {// 新規作成時の初期化
        form.resetFields();

        if (initialValue === undefined) {
            presetFieldsValue();
        }
    }, [initialValue]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {// 編集時の初期化
        // source.appTemplatesForAdmin が読み込まれてから初期化する
        if (initialValue === undefined || loading) return;

        form.setFieldsValue({
            ...initialValue,
            applicationTemplateUuid: initialValue.ltiTemplate?.template?.uuid,
            applicationUrlValueCandidateUuid: initialValue.ltiTemplate?.candidate?.uuid,
            targetPermissions: initialValue.targetPermissions.map(v => v.code),
        });

        const selectedChoiceTemplate = findVer13ChangeableChoiceTemplateByUuid(
            source.appTemplatesForAdmin.data.items,
            initialValue.ltiTemplate?.template?.uuid
        );

        setCurrentTarget({
            target_organizations: initialValue.targetOrganizations
        });
        setIsPublishToAllOrganizations(initialValue.isPublishToAllOrganizations);
        setIsDesignatedTerm(initialValue.isDesignatedTerm)
        setSelectedChoiceTemplate(selectedChoiceTemplate);
        if (!isCreate) {
            const formFieldsValue = form.getFieldsValue();
            setFormInitValue({
                ...formFieldsValue,
                target: {
                    target_organization: initialValue.targetOrganizations,
                },
                duration: convertDateToString(formFieldsValue.duration),
            });
            setIsDisabledSubmitBtn(true);
        }
    }, [loading]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (visible && !source.appCategory.loading && source.appCategory.status === Actions.statusEnum.SUCCESS) {
            onOk();
        }
    }, [source.appCategory.loading]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isCreate && visible) {
            const isFormChange = checkFormChange(formInitValue, formChangeValue);
            setIsDisabledSubmitBtn(!isFormChange);
        }
    }, [formChangeValue]);

    const onFinish = (values) => {
        const postBody = applicationUtils.convertApplicationCategoryToPostBody(
            values,
            initialValue,
            isCreate ? null : initialValue.order,
            currentTarget
        );

        if (isCreate) {
            dispatch(Actions.http.connection.apps.category.controlCreate(postBody));
        } else {
            dispatch(Actions.http.connection.apps.category.controlUpdate(initialValue.uuid, postBody));
        }
    };

    const handleSelectTargetSubmit = (values) => {
        setIsOpenDeliveryTargetModal(false);
        setCurrentTarget(values);
        form.setFieldsValue({ target: { target_organizations: values.target_organizations } });
        handleValuesChange(form.getFieldsValue());
    };
    const handleSelectTargetCancel = () => setIsOpenDeliveryTargetModal(false);

    const handleSubmit = () => {
        form.submit();
    };

    const handleSelectUseDeepLinking = (event) => {
        if (event.target.value) return;

        form.setFieldsValue({
            applicationTemplateUuid: undefined,
            applicationUrlValueCandidateUuid: undefined,
        });
        setSelectedChoiceTemplate(undefined);
    };

    const handleApplicationTemplateUuid = (uuid) => {
        const selectedChoiceTemplate = source.appTemplatesForAdmin.data.items
            .filter(temp => temp.type === 2) // LTI 1.3
            .filter(temp => temp.url.is_choices) // 選択式
            .find(temp => temp.uuid === uuid);

        setSelectedChoiceTemplate(selectedChoiceTemplate);

        const defaultCandidateUuid = selectedChoiceTemplate ?
            selectedChoiceTemplate.url.candidates.find(candidate => candidate.is_default).uuid :
            undefined;

        form.setFieldsValue({ applicationUrlValueCandidateUuid: defaultCandidateUuid });
    };

    const handleCancel = () => {
        onCancel();
        if (!isCreate) {
            setIsDisabledSubmitBtn(false);
        }
    };

    const handleValuesChange = (valuesChange) => {
        if (!isCreate) {
            const { deliveryTargets, ...values } = valuesChange;
            setFormChangeValue({
                ...values,
                target: valuesChange.target || {
                    target_organization: currentTarget.target_organizations,
                },
                duration: convertDateToString(values.duration),
            });
        }
    };

    return (
        <BaseModal
            className='common-modal'
            title={isCreate ? <span>教材・アプリグループの新規作成</span> : <span>教材・アプリグループの編集</span>}
            visible={visible}
            onCancel={handleCancel}
            forceRender
            footer={[
                <Button key='back'
                    size='large'
                    loading={source.appCategory.loading || loading}
                    onClick={handleCancel}
                >
                    キャンセル
                </Button>,
                <Button key='create'
                    type='primary'
                    size='large'
                    loading={source.appCategory.loading || loading}
                    disabled={isDisabledSubmitBtn}
                    onClick={handleSubmit}
                >
                    保存
                </Button>
            ]}
            style={{ top: 20 }}
            maskClosable={false}
        >
            <Spin indicator={antIcon} spinning={loading}>
                <Form {...layout} form={form} name='control-hooks-edit-app-groups' onFinish={onFinish}
                    onValuesChange={(_, allValues) => handleValuesChange(allValues)}
                >
                    <Form.Item name='title'
                        label='教材・アプリグループ名称（小学校3年生以上）'
                        rules={[
                            {
                                required: true,
                                message: '教材・アプリグループ名称（小学校3年生以上）を入力してください'
                            },
                            applicationFormRules.applicationCategoryTitleRange(),
                        ]}
                    >
                        <Input placeholder='教材・アプリグループ名称を入力してください'/>
                    </Form.Item>

                    <Form.Item name='titleForLower'
                        label='教材・アプリグループ名称（小学校1・2年生）'
                        rules={[
                            {
                                required: true,
                                message: '教材・アプリグループ名称（小学校1・2年生）を入力してください'
                            },
                            applicationFormRules.applicationCategoryTitleRange(),
                        ]}
                    >
                        <Input placeholder='教材・アプリグループ名称を入力してください'/>
                    </Form.Item>

                    <Form.Item name='description'
                        label='概要'
                        rules={[
                            applicationFormRules.applicationCategoryDescriptionRange(),
                        ]}
                    >
                        <Input placeholder='概要を入力してください'/>
                    </Form.Item>

                    <Form.Item name='isPublishToAllOrganizations'
                        label='配信対象'
                        rules={[{ required: true, message: '配信対象を選択してください' }]}
                    >
                        <Radio.Group onChange={(e) => { setIsPublishToAllOrganizations(e.target.value); }}>
                            <Radio value={true}>すべて</Radio>
                            <Radio value={false}>個別指定</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {!isPublishToAllOrganizations ? (
                        <Form.Item label='' name='deliveryTargets'{...formItemLayoutWithoutLabel}>
                            <>
                                <Button type='primary' icon={<TeamOutlined />} onClick={() => setIsOpenDeliveryTargetModal(true)}>
                                    配信対象を選択
                                </Button>
                                <Targets.Detail currentTarget={currentTarget} />
                            </>
                        </Form.Item>
                    ) : undefined}

                    <Form.Item name='target' value={currentTarget} hidden><Input /></Form.Item>
                    <Form.Item name='duration'
                        label='公開期間'
                        rules={[formRules.requiredDuration({ label: '公開期間' })]}
                    >
                        <CustomRangePicker
                            form={form}
                            name='duration'
                            locale={jaJP}
                            showTime={{ format: 'HH:mm' }}
                            format='YYYY/MM/DD HH:mm'
                            disabledDate={getDisabledDate}
                            ranges={DatePickerRangesIncludeInfinite()}
                        />
                    </Form.Item>

                    <Form.Item name='targetPermissions'
                        label={texts.targetPermissionsTitle}
                        value={0}
                        rules={[{ required: true, message: `${texts.targetPermissionsTitle}を選択してください` }]}
                    >
                        <Checkbox.Group style={{ width: '100%' }}>
                            <Checkbox value='administrator'>{texts.targetPermissionsCheckbox.administrator}</Checkbox>
                            <Checkbox value='teacher'>{texts.targetPermissionsCheckbox.teacher}</Checkbox>
                            <Checkbox value='student'>{texts.targetPermissionsCheckbox.student}</Checkbox>
                        </Checkbox.Group>
                    </Form.Item>

                    <Form.Item name='targetGrades'
                        label='公開対象学年コード'
                        rules={[
                            { required: true, message: '公開対象学年コードを選択してください' },
                            formRules.targetGradesValidator,
                        ]}
                    >
                        <TargetGradesSelect
                            placeholder='学年コードを選択してください'
                            allowClear
                            showSearch={false}
                        />
                    </Form.Item>

                    <Form.Item label='年度指定' name='isDesignatedTerm'>
                        <Radio.Group
                            onChange={e => { setIsDesignatedTerm(e.target.value); }}
                        >
                            <Radio value={true}>あり</Radio>
                            <Radio value={false}>なし</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        hidden={!isDesignatedTerm}
                        label='年度選択'
                        name='targetTerm'
                        rules={isDesignatedTerm ?
                            [{ message: '年度を選択してください', required: true }] :
                            []
                        }
                    >
                        <Select
                            allowClear
                            placeholder='年度を選択してください'
                            showSearch={false}
                        >
                            {globalSelector.termList.map((value, index) => (
                                <Option key={index} value={value.uuid}>
                                    {value.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name='canAdminSetPublic'
                        label={
                            <span>
                                学校管理画面での公開期間変更
                                <Tooltip title='アプリグループの配信先の学校で、学校管理者が公開期間を任意に変更できるかどうかを設定できます'>
                                    <QuestionCircleOutlined style={{ marginLeft: '0.5rem' }} />
                                </Tooltip>
                            </span>
                        }
                        rules={[{ required: true, message: '学校管理者での公開を選択してください' }]}
                    >
                        <Radio.Group>
                            <Radio value={false}>許可しない</Radio>
                            <Radio value={true}>許可する</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <FormCard
                        ghost
                        title={
                            <div>
                                高度な接続
                                <Tooltip title='LTI Deep Linking を用いた教材・アプリの登録をするときに設定します。'>
                                    <QuestionCircleOutlined style={{ marginLeft: '0.5rem' }} />
                                </Tooltip>
                            </div>
                        }
                        noHeaderStyle
                        isView={visible}
                        isOpen={useDeepLinking}
                    >
                        <Form.Item name='useDeepLinking'
                            label='LTI Deep Linking でアプリを追加'
                            rules={[{ required: true }]}
                        >
                            <Radio.Group
                                disabled={!isCreate}
                                onChange={handleSelectUseDeepLinking}
                            >
                                <Radio value={false}>しない</Radio>
                                <Radio value={true}>する</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {useDeepLinking ? (
                            <Form.Item name='applicationTemplateUuid'
                                label='LTI1.3アプリ選択'
                                rules={[
                                    { required: true, message: 'LTI1.3アプリを選択してください' },
                                ]}
                            >
                                <Select
                                    placeholder='LTI1.3アプリを選択してください'
                                    allowClear
                                    disabled={!isCreate}
                                    onSelect={handleApplicationTemplateUuid}
                                >
                                    {source.appTemplatesForAdmin.data.items.map(template => (
                                        <Option key={template.uuid} value={template.uuid}>
                                            {template.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        ) : undefined}

                        {selectedChoiceTemplate ? (
                            <Form.Item name='applicationUrlValueCandidateUuid'
                                // リスト選択不可の場合でも application_url_value_candidate_uuid を送る必要があるので hidden とする
                                hidden={!selectedChoiceTemplate?.url?.changeable_param_control}
                                label='接続パラメータ'
                                rules={[
                                    { required: true, message: '接続パラメータを選択してください' },
                                ]}
                            >
                                <Select
                                    placeholder='接続パラメータを選択してください'
                                    allowClear
                                    disabled={!isCreate}
                                >
                                    {selectedChoiceTemplate?.url?.candidates?.map(candidate => (
                                        <Option key={candidate.uuid} value={candidate.uuid}>
                                            {candidate.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        ) : undefined}
                    </FormCard>
                </Form>
            </Spin>

            <Targets
                showClass={false}
                showUser={false}
                showOrganization={true}
                visible={isOpenDeliveryTargetModal}
                currentTarget={currentTarget}
                onCancel={handleSelectTargetCancel}
                onSubmit={handleSelectTargetSubmit}
            />
        </BaseModal>
    );
};

export default AppGroupModal;

/**
 * templates の中から、
 * LTI 1.3 で選択式のもののうち、 uuid が一致するものを見つける
 */
const findVer13ChangeableChoiceTemplateByUuid = (templates, uuid) => {
    return templates
        .filter(temp => temp.type === 2) // LTI 1.3
        .filter(temp => temp.url.is_choices) // 選択式
        .find(temp => temp.uuid === uuid);
};